import React, { useEffect, useState } from "react";
import { DialogContent, Grid } from "@mui/material";
import { AxiosError } from "axios";

import BarekamInputMultilingual from "../../../../../../pages/components/input-multilingual/barekam-input-multilingual";
import { StandardDialogFooter } from "../../../../../../pages/components/dialogs/standard-dialog/standard-dialog";
import { mapValidationErrors } from "../../../../../../pages/user/settings/personal-info/personal-info.service";
import {
  Message,
  MessageSections,
} from "../../../../../../pages/components/message/message";
import { BarekamAttachment } from "../../../../../../pages/components/attachment/attachment";
import BarekamInput from "../../../../../../pages/components/input/barekam-input";
import { BlogPostModel } from "../../../../../../pages/shared/models/blogModel";
import { getMessage } from "../../../../../../pages/shared/helper-functions";
import BarekamCheckbox from "../../../../../../pages/components/checkbox";
import BarekamText from "../../../../../../pages/components/text";

import { useAppSelector } from "../../../../../../redux/store";
import blogService from "../../../../../../services/blog-service";
import { CategoryModel } from "../../../../../../pages/shared/models/CategoryModel";
import blogCategoryService from "../../../../../../services/blog-category-service";
import { BarekamSelect } from "../../../../../../pages/components/select/select";
import { BarekamMenuItem } from "../../../../../../pages/components/select/styled/orders";
import { CHECKBOX_STATUS } from "../../../../../../constants";

const EditBlog = ({
  blogData,
  onCancel,
}: {
  blogData: BlogPostModel;
  onCancel: () => void;
}) => {
  const [modifiedBlog, setModifiedBlog] = useState(blogData);
  const [blogCategories, setBlogCategories] = useState<CategoryModel[] | []>(
    [],
  );
  const [file, setFile] = useState<File>();
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const { messageCache, languageId } = useAppSelector(
    (state) => state.messagesReducer,
  );
  const [isChecked, setIsChecked] = useState(blogData.featured);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await blogCategoryService.getBlogCategories(
          undefined,
          20,
          0,
        );
        setBlogCategories(response.data);
      } catch (err) {
        console.log("Failed to fetch categories.");
      }
    };
    fetchCategories();
  }, []);

  function handleChange(
    field:
      | "title"
      | "description"
      | "shortDescription"
      | "slug"
      | "fileId"
      | "featured"
      | "categoryIds",
    value: any,
  ) {
    if (field === 'featured') {
      setIsChecked(!isChecked);
    }
    if (field === 'categoryIds') {
      setModifiedBlog((prevState) => {
        return { ...prevState, [field]: [value] };
      });
      return;
    }
    setModifiedBlog((prevState) => {
      return { ...prevState, [field]: value };
    });
  }

  function validateData() {
    const errors = {} as { [key: string]: string };
    const requiredFields = ["slug", "title", "description", "featured", "shortDescription"];

    for (const field of requiredFields) {
      const key = field as keyof typeof modifiedBlog;
      if (
        modifiedBlog[key] === undefined ||
        modifiedBlog[key] === null ||
        modifiedBlog[key] === ""
      ) {
        errors[key] = "required";
      }
    }
    return errors;
  }

  function handleSave() {
    const errors = validateData();
    setErrors(errors);

    if (Object.keys(errors).length === 0) {
      const formData = new FormData();
      if (file?.name) {
        formData.append("file", file);
      }
      formData.append("data", JSON.stringify(modifiedBlog));

      if (blogData.id) {
        blogService
          .editPost(blogData.id, formData)
          .then(() => onCancel())
          .catch((reason) => {
            if (reason instanceof AxiosError) {
              setErrors(mapValidationErrors(reason));
            }
          });
      } else {
        blogService
          .createPost(formData)
          .then(() => onCancel())
          .catch((reason) => {
            if (reason instanceof AxiosError) {
              setErrors(mapValidationErrors(reason));
            }
          });
      }
    }
  }

  return (
    <>
      <DialogContent>
        <Grid container rowSpacing={"1rem"} columnSpacing={"1rem"}>
          <BarekamInputMultilingual
            multilingualText={modifiedBlog.title || {}}
            labelKey={"title"}
            field={"title"}
            onChange={(title) => handleChange("title", title)}
            errors={errors}
          />

          <BarekamInputMultilingual
            multilingualText={modifiedBlog.description || {}}
            labelKey={"description"}
            field={"description"}
            onChange={(description) => handleChange("description", description)}
            errors={errors}
          />

          <BarekamInputMultilingual
            multilingualText={modifiedBlog.shortDescription || {}}
            labelKey={"shortDescription"}
            field={"shortDescription"}
            onChange={(shortDescription) => handleChange("shortDescription", shortDescription)}
            errors={errors}
          />

          <Grid item xs={12}>
            <BarekamInput
              value={modifiedBlog.slug}
              key={"slug"}
              placeholder={getMessage(
                "slug",
                MessageSections.MAIN,
                messageCache,
                languageId,
              )}
              customInputVariant={"standard"}
              width={"100%"}
              error={!!errors["slug"]}
              errorMessage={
                errors["slug"] &&
                getMessage(
                  errors["slug"],
                  MessageSections.MAIN,
                  messageCache,
                  languageId,
                )
              }
              onChange={(event) => handleChange("slug", event.target.value)}
            />
          </Grid>

          <Grid item xs={12}>
            <BarekamSelect
              variant={"outlined"}
              label={
                <BarekamText variant={"1rem"}>
                  <Message
                    messageKey={"category"}
                    sectionId={MessageSections.ORDERS}
                  />
                </BarekamText>
              }
              value={modifiedBlog?.categoryIds || ""}
              type={"standard"}
              error={!!errors["categoryIds"]}
              errorMessage={
                errors["categoryIds"] &&
                getMessage(
                  errors["categoryIds"],
                  MessageSections.ORDERS,
                  messageCache,
                  languageId,
                )
              }
              fullWidth
              required
            >
              {blogCategories?.map((list: CategoryModel) => {
                return (
                  <BarekamMenuItem
                    key={list.id}
                    value={list.id}
                    onClick={() => handleChange("categoryIds", list.id)}
                  >
                    {list.name[languageId]}
                  </BarekamMenuItem>
                );
              })}
            </BarekamSelect>
          </Grid>

          <Grid item xs={12}>
            <BarekamAttachment
              titleLabel={
                <Message
                  messageKey={"featured_image"}
                  sectionId={MessageSections.MAIN}
                />
              }
              value={file}
              isSingle={true}
              onValueChange={(value: File) => setFile(value)}
            />
          </Grid>
          <Grid item xs={12}>
            <img src={modifiedBlog?.imgUrl} width={200} height={100} />
          </Grid>
          <Grid item xs={12}>
            <BarekamCheckbox
              checked={isChecked}
              onChange={(event) => handleChange("featured", event.target.checked)}
            />
            <BarekamText variant={"m"} opaque>
              <Message
                messageKey={"featured"}
                sectionId={MessageSections.MAIN}
              />
            </BarekamText>
          </Grid>
        </Grid>
      </DialogContent>

      <StandardDialogFooter
        onCancel={() => onCancel()}
        onSave={() => handleSave()}
        saveLabel={
          <Message messageKey={"save"} sectionId={MessageSections.MAIN} />
        }
      />
    </>
  );
};

export default EditBlog;
